/**
 * TiledMapLoader.js - Tiled Map Editor TMJ Loader
 *
 * Parses Tiled JSON (.tmj) map files and provides:
 * - Tile layer data for ground/floor rendering
 * - Object layer data for static objects (props, buildings)
 * - Collision data for physics system
 * - Tileset management with GID resolution
 *
 * TMJ File Structure:
 * - Map contains layers (tile layers and object layers)
 * - Tile layers have a flat data array of GIDs
 * - Object layers have positioned objects with optional GIDs
 * - Tilesets define image sources and tile dimensions
 */

import { ClientLogger } from '../logger/ClientLogger.js';

const tiledMapLogger = (() => {
  if (ClientLogger) {
    return ClientLogger.getLogger('map');
  }
  if (typeof console !== 'undefined') {
    return console;
  }
  return { log: () => {}, info: () => {}, warn: () => {}, error: () => {} };
})();

const TiledMapLoader = {
  // Loaded map data
  mapData: null,

  // Map dimensions
  mapWidth: 0, // in tiles
  mapHeight: 0, // in tiles
  tileWidth: 0,
  tileHeight: 0,
  worldWidth: 0, // in pixels
  worldHeight: 0, // in pixels

  // Parsed tilesets (array of tileset info with GID ranges)
  tilesets: [],

  // Parsed layers
  tileLayers: [], // Ground/floor tile layers
  objectLayers: [], // Object layers with static props

  // Cached collision objects (for physics system)
  collisionObjects: [],

  // Asset base path (prepended to tileset paths)
  assetBasePath: '',

  /**
   * Load a Tiled map from a TMJ URL
   * @param {string} url - URL to the TMJ file
   * @param {string} assetBasePath - Base path for assets (default: '')
   * @returns {Promise<Object>} Parsed map data
   */
  async load(url, assetBasePath = '') {
    this.assetBasePath = assetBasePath;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load map: ${response.status} ${response.statusText}`);
      }

      this.mapData = await response.json();
      this._parseMapData();

      tiledMapLogger.info('[TiledMapLoader] Map loaded', {
        tiles: { width: this.mapWidth, height: this.mapHeight },
        pixels: { width: this.worldWidth, height: this.worldHeight }
      });
      tiledMapLogger.info('[TiledMapLoader] Layer stats', {
        tilesets: this.tilesets.length,
        tileLayers: this.tileLayers.length,
        objectLayers: this.objectLayers.length
      });

      return {
        width: this.worldWidth,
        height: this.worldHeight,
        tileLayers: this.tileLayers,
        objectLayers: this.objectLayers,
        collisionObjects: this.collisionObjects,
        tilesets: this.tilesets
      };
    } catch (error) {
      tiledMapLogger.error('[TiledMapLoader] Error loading map', { error });
      throw error;
    }
  },

  /**
   * Parse the raw map data
   * @private
   */
  _parseMapData() {
    const map = this.mapData;

    // Map dimensions
    this.mapWidth = map.width;
    this.mapHeight = map.height;
    this.tileWidth = map.tilewidth;
    this.tileHeight = map.tileheight;
    
    // For infinite maps, calculate actual dimensions from chunks
    if (map.infinite) {
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      
      for (const layer of map.layers) {
        if (layer.type === 'tilelayer' && layer.chunks) {
          for (const chunk of layer.chunks) {
            minX = Math.min(minX, chunk.x);
            minY = Math.min(minY, chunk.y);
            maxX = Math.max(maxX, chunk.x + chunk.width);
            maxY = Math.max(maxY, chunk.y + chunk.height);
          }
        }
      }
      
      // Calculate world dimensions from chunk bounds
      if (minX !== Infinity) {
        this.mapWidth = maxX - minX;
        this.mapHeight = maxY - minY;
      }
    }
    
    this.worldWidth = this.mapWidth * this.tileWidth;
    this.worldHeight = this.mapHeight * this.tileHeight;

    // Parse tilesets
    this._parseTilesets();

    // Parse layers
    this.tileLayers = [];
    this.objectLayers = [];
    this.collisionObjects = [];

    for (const layer of map.layers) {
      if (layer.type === 'tilelayer') {
        this._parseTileLayer(layer);
      } else if (layer.type === 'objectgroup') {
        this._parseObjectLayer(layer);
      }
    }
  },

  /**
   * Parse tilesets from map data
   * Each tileset has a firstgid (first global ID) that maps to its tiles
   * @private
   */
  _parseTilesets() {
    this.tilesets = [];

    for (const ts of this.mapData.tilesets) {
      const tileset = {
        name: ts.name,
        firstgid: ts.firstgid,
        tileWidth: ts.tilewidth,
        tileHeight: ts.tileheight,
        tileCount: ts.tilecount,
        columns: ts.columns,
        // Image path (for tilesheet tilesets)
        image: ts.image ? this.assetBasePath + ts.image : null,
        imageWidth: ts.imagewidth,
        imageHeight: ts.imageheight,
        // Individual tile images (for collection tilesets)
        tiles: new Map()
      };

      // Calculate last GID for this tileset
      tileset.lastgid = tileset.firstgid + tileset.tileCount - 1;

      // Parse individual tile definitions (for collection tilesets)
      if (ts.tiles) {
        for (const tile of ts.tiles) {
          const globalId = tileset.firstgid + tile.id;
          tileset.tiles.set(globalId, {
            id: tile.id,
            image: tile.image ? this.assetBasePath + tile.image : null,
            imageWidth: tile.imagewidth,
            imageHeight: tile.imageheight,
            properties: this._parseProperties(tile.properties)
          });
        }
      }

      this.tilesets.push(tileset);
    }

    // Sort tilesets by firstgid for efficient lookup
    this.tilesets.sort((a, b) => a.firstgid - b.firstgid);
  },

  /**
   * Parse a tile layer
   * Tile layers contain GIDs representing the ground/floor
   * Supports both infinite maps (chunks) and finite maps (flat data array)
   * @private
   */
  _parseTileLayer(layer) {
    const tileLayer = {
      id: layer.id,
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity,
      width: layer.width,
      height: layer.height,
      // Data is a flat array of GIDs (0 = empty, otherwise tileset GID)
      data: layer.data,
      // Pre-compute tile positions for efficient rendering
      tiles: []
    };

    // Check if this is an infinite map with chunks
    if (layer.chunks && layer.chunks.length > 0) {
      // Infinite map - process chunks
      for (const chunk of layer.chunks) {
        const chunkX = chunk.x;
        const chunkY = chunk.y;
        const chunkWidth = chunk.width;
        const chunkHeight = chunk.height;
        
        for (let y = 0; y < chunkHeight; y++) {
          for (let x = 0; x < chunkWidth; x++) {
            const index = y * chunkWidth + x;
            const gid = chunk.data[index];

            if (gid === 0) continue; // Empty tile

            const tileInfo = this._resolveTileGid(gid);
            if (tileInfo) {
              // Calculate world position (chunk position + tile position within chunk)
              const worldX = (chunkX + x) * this.tileWidth;
              const worldY = (chunkY + y) * this.tileHeight;
              
              tileLayer.tiles.push({
                x: worldX,
                y: worldY,
                width: this.tileWidth,
                height: this.tileHeight,
                gid: gid,
                tilesetName: tileInfo.tilesetName,
                // For tilesheet: source rectangle in tilesheet
                srcX: tileInfo.srcX,
                srcY: tileInfo.srcY,
                srcWidth: tileInfo.srcWidth,
                srcHeight: tileInfo.srcHeight,
                // Tilesheet image path
                image: tileInfo.image
              });
            }
          }
        }
      }
    } else if (layer.data && layer.data.length > 0) {
      // Finite map - process flat data array
      for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
          const index = y * layer.width + x;
          const gid = layer.data[index];

          if (gid === 0) continue; // Empty tile

          const tileInfo = this._resolveTileGid(gid);
          if (tileInfo) {
            tileLayer.tiles.push({
              x: x * this.tileWidth,
              y: y * this.tileHeight,
              width: this.tileWidth,
              height: this.tileHeight,
              gid: gid,
              tilesetName: tileInfo.tilesetName,
              // For tilesheet: source rectangle in tilesheet
              srcX: tileInfo.srcX,
              srcY: tileInfo.srcY,
              srcWidth: tileInfo.srcWidth,
              srcHeight: tileInfo.srcHeight,
              // Tilesheet image path
              image: tileInfo.image
            });
          }
        }
      }
    }

    this.tileLayers.push(tileLayer);
  },

  /**
   * Parse an object layer
   * Object layers contain positioned objects (props, buildings, spawn points, etc.)
   * @private
   */
  _parseObjectLayer(layer) {
    const objectLayer = {
      id: layer.id,
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity,
      objects: []
    };

    for (const obj of layer.objects) {
      const properties = this._parseProperties(obj.properties);

      // IMPORTANT: Tiled coordinate systems differ based on object type:
      // - Tile objects (with gid): x,y is the BOTTOM-LEFT corner
      // - Shape objects (rectangles, polygons without gid): x,y is the TOP-LEFT corner
      // We need to convert to CENTER coordinates for Matter.js physics
      let centerX = obj.x + obj.width / 2;
      let centerY;
      
      if (obj.gid) {
        // Tile object: y is bottom of object, so center is y - height/2
        centerY = obj.y - obj.height / 2;
      } else {
        // Shape object (rectangle, polygon): y is top of object, so center is y + height/2
        centerY = obj.y + obj.height / 2;
      }

      const parsedObj = {
        id: obj.id,
        name: obj.name || '',
        type: obj.type || '',
        x: centerX,
        y: centerY,
        width: obj.width,
        height: obj.height,
        rotation: obj.rotation || 0, // In degrees
        visible: obj.visible,
        gid: obj.gid, // Global tile ID (if using a tileset image)
        properties: properties,
        // Collision properties
        collidable: properties.collidable === 'true' || properties.collidable === true,
        isSensor: properties.isSensor === 'true' || properties.isSensor === true,
        canJumpOver: properties.can_jump_over === 'true' || properties.can_jump_over === true
      };

      // Resolve image from GID
      if (obj.gid) {
        const tileInfo = this._resolveTileGid(obj.gid);
        if (tileInfo) {
          parsedObj.image = tileInfo.image;
          parsedObj.srcX = tileInfo.srcX;
          parsedObj.srcY = tileInfo.srcY;
          parsedObj.srcWidth = tileInfo.srcWidth;
          parsedObj.srcHeight = tileInfo.srcHeight;
        }
      }

      objectLayer.objects.push(parsedObj);

      // Add to collision objects if collidable
      if (parsedObj.collidable) {
        this.collisionObjects.push({
          id: `tiled_${parsedObj.id}`,
          name: parsedObj.name,
          type: parsedObj.type || 'obstacle',
          x: parsedObj.x,
          y: parsedObj.y,
          width: parsedObj.width,
          height: parsedObj.height,
          rotation: parsedObj.rotation,
          collidable: true,
          isSensor: parsedObj.isSensor,
          canJumpOver: parsedObj.canJumpOver
        });
      }
    }

    this.objectLayers.push(objectLayer);
  },

  /**
   * Resolve a Global Tile ID (GID) to its tileset and source coordinates
   * @param {number} gid - Global tile ID
   * @returns {Object|null} Tile info with image and source coordinates
   * @private
   */
  _resolveTileGid(gid) {
    if (gid === 0) return null;

    // Find the tileset that contains this GID
    let tileset = null;
    for (let i = this.tilesets.length - 1; i >= 0; i--) {
      if (gid >= this.tilesets[i].firstgid) {
        tileset = this.tilesets[i];
        break;
      }
    }

    if (!tileset) return null;

    // Check if this is a collection tileset with individual tile images
    if (tileset.tiles.has(gid)) {
      const tile = tileset.tiles.get(gid);
      return {
        tilesetName: tileset.name,
        image: tile.image,
        srcX: 0,
        srcY: 0,
        srcWidth: tile.imageWidth,
        srcHeight: tile.imageHeight,
        properties: tile.properties
      };
    }

    // Standard tilesheet tileset - calculate source rectangle
    const localId = gid - tileset.firstgid;
    const columns = tileset.columns || 1;

    const srcX = (localId % columns) * tileset.tileWidth;
    const srcY = Math.floor(localId / columns) * tileset.tileHeight;

    return {
      tilesetName: tileset.name,
      image: tileset.image,
      srcX: srcX,
      srcY: srcY,
      srcWidth: tileset.tileWidth,
      srcHeight: tileset.tileHeight
    };
  },

  /**
   * Parse Tiled properties array into an object
   * @param {Array} propsArray - Array of property objects
   * @returns {Object} Properties as key-value pairs
   * @private
   */
  _parseProperties(propsArray) {
    if (!propsArray) return {};

    const props = {};
    for (const prop of propsArray) {
      props[prop.name] = prop.value;
    }
    return props;
  },

  /**
   * Get collision objects for the physics system
   * @returns {Array} Array of collision objects
   */
  getCollisionObjects() {
    return this.collisionObjects;
  },

  /**
   * Get all visible objects from object layers
   * @returns {Array} Array of all objects
   */
  getAllObjects() {
    const objects = [];
    for (const layer of this.objectLayers) {
      if (layer.visible) {
        objects.push(...layer.objects);
      }
    }
    return objects;
  },

  /**
   * Get tile layer by name
   * @param {string} name - Layer name
   * @returns {Object|null} Tile layer
   */
  getTileLayer(name) {
    return this.tileLayers.find((layer) => layer.name === name) || null;
  },

  /**
   * Get object layer by name
   * @param {string} name - Layer name
   * @returns {Object|null} Object layer
   */
  getObjectLayer(name) {
    return this.objectLayers.find((layer) => layer.name === name) || null;
  },

  /**
   * Get spawn points (if defined in map)
   * @param {string} layerName - Name of layer containing spawn points
   * @param {string} type - Optional type filter
   * @returns {Array} Array of spawn point objects
   */
  getSpawnPoints(layerName = 'spawn_points', type = null) {
    const layer = this.getObjectLayer(layerName);
    if (!layer) return [];

    return layer.objects.filter((obj) => {
      if (type) {
        return obj.type === type || obj.name.toLowerCase().includes(type.toLowerCase());
      }
      return true;
    });
  },

  /**
   * Clear loaded map data
   */
  clear() {
    this.mapData = null;
    this.tilesets = [];
    this.tileLayers = [];
    this.objectLayers = [];
    this.collisionObjects = [];
    this.mapWidth = 0;
    this.mapHeight = 0;
    this.worldWidth = 0;
    this.worldHeight = 0;
  }
};

// Export for use in other modules

export { tiledMapLogger, TiledMapLoader };
